/* ProtectedRoute — role-aware gate.
 *
 *   <ProtectedRoute role="admin">      ↦ AdminDashboard
 *   <ProtectedRoute role="customer">   ↦ CustomerDashboard
 *
 * For role="admin":
 *   - admin authed     → render
 *   - admin unauthed   → redirect /admin/login?next=<original>
 *
 * For role="customer":
 *   - customer authed  → render
 *   - customer unauthed AND admin authed → render (admins can preview)
 *   - both unauthed    → redirect /customer/login?next=<original>
 *
 * Also injects a `noindex, nofollow` meta tag for the route lifetime so
 * search engines never crawl authenticated views.
 */
import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";

const Spinner = ({ label }) => (
  <div
    className="min-h-screen grid place-items-center bg-navy text-gold"
    data-testid="auth-checking"
  >
    <div className="flex flex-col items-center gap-3">
      <div className="h-12 w-12 rounded-2xl bg-gold/15 grid place-items-center border border-gold/30">
        <ShieldCheck className="h-6 w-6 text-gold" />
      </div>
      <div className="inline-flex items-center gap-2 text-sm font-bold tracking-wide">
        <Loader2 className="h-4 w-4 animate-spin" /> {label}
      </div>
    </div>
  </div>
);

const ProtectedRoute = ({ role = "admin", children }) => {
  const { adminStatus, customerStatus } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const tag = document.createElement("meta");
    tag.name = "robots";
    tag.content = "noindex, nofollow";
    document.head.appendChild(tag);
    return () => {
      document.head.removeChild(tag);
    };
  }, []);

  if (role === "admin") {
    if (adminStatus === "checking") return <Spinner label="Verifying admin session…" />;
    if (adminStatus !== "authenticated") {
      // If a logged-in customer landed on /admin we still redirect them
      // to the admin login (the explicit 403 is reserved for users who
      // try to use the admin API while authenticated as a customer).
      const next = encodeURIComponent(location.pathname + location.search);
      return <Navigate to={`/admin/login?next=${next}`} replace />;
    }
    return children;
  }

  // role === "customer"
  if (customerStatus === "checking" || adminStatus === "checking") {
    return <Spinner label="Loading your dashboard…" />;
  }
  if (customerStatus === "authenticated" || adminStatus === "authenticated") {
    return children;
  }
  const next = encodeURIComponent(location.pathname + location.search);
  return <Navigate to={`/customer/login?next=${next}`} replace />;
};

export default ProtectedRoute;
